import { Typography } from "antd";
import { clsx } from "clsx";

import styles from "./RVSimpleTextFloatingPlaceholder.module.scss";

/** Props for {@link RVSimpleTextFloatingPlaceholder}. */
export type RVSimpleTextFloatingPlaceholderProps = {
    /** The text shown as a placeholder / floating label. */
    placeholderText: string;
    /** Whether the editor content is currently empty. */
    isTextEditorEmpty: boolean;
    /** Force the label into its "floating" (minimized) state. */
    displayFloatingLabel: boolean;
    /** Constrains label positioning for single-line editors. */
    isSingleLine?: boolean;
    /** Click handler forwarded to the wrapper `<div>`. */
    onClick?: () => void;
};

/**
 * Floating label / placeholder for a Lexical `SimpleTextEditor`.
 *
 * Transitions between full-size placeholder text and a minimized floating
 * label based on editor content state.
 *
 * @example
 * ```tsx
 * <RVSimpleTextFloatingPlaceholder
 *   placeholderText="Enter value"
 *   isTextEditorEmpty={isEmpty}
 *   displayFloatingLabel={hasFocus}
 * />
 * ```
 */
export function RVSimpleTextFloatingPlaceholder({
    placeholderText,
    isTextEditorEmpty,
    displayFloatingLabel,
    isSingleLine,
    onClick = () => {},
}: RVSimpleTextFloatingPlaceholderProps): JSX.Element {
    return (
        <div onClick={onClick}>
            <Typography.Text
                className={clsx({
                    [styles.placeholderText]: !displayFloatingLabel && isTextEditorEmpty,
                    [styles.placeholderTextMinimized]: !isTextEditorEmpty || displayFloatingLabel,
                    [styles.singleLine]: isSingleLine,
                    [styles.multiLine]: !isSingleLine,
                })}
            >
                {placeholderText}
            </Typography.Text>
        </div>
    );
}
